import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Wallet, Settings2, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney, PAYMENT_METHODS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INST_STATUS, fmtDate, todayIso } from "@/lib/ortho";

function PayDialog({ inst, onOpenChange, onDone }) {
  const [method, setMethod] = useState("efectivo");
  const [amount, setAmount] = useState("");
  const [cashOpen, setCashOpen] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!inst) return;
    setMethod("efectivo"); setAmount(String(inst.amount || "")); setCashOpen(null);
    api.get("/cashbox/today").then((r) => setCashOpen(!!(r.data.session && r.data.session.status === "abierta"))).catch(() => setCashOpen(false));
  }, [inst]);
  const pay = async () => {
    setLoading(true);
    try {
      await api.post(`/ortho/installments/${inst.id}/pay`, { payment_method: method, amount: Number(amount) || 0 });
      toast.success(`Pago de ${formatMoney(Number(amount))} registrado en caja`);
      onOpenChange(false); onDone && onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={!!inst} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ortho-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-primary" /> Registrar pago · {inst?.label}</DialogTitle>
          <DialogDescription>Vence {fmtDate(inst?.due_date)}. El pago se suma a la caja del día.</DialogDescription>
        </DialogHeader>
        {cashOpen === false && (
          <div className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="ortho-cash-closed">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span><b>La caja está cerrada.</b> Ábrela en Caja → Abrir caja para registrar este pago.</span>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`ortho-pay-method-${m.key}`}
                className={`h-9 rounded-sm text-[10px] uppercase tracking-widest font-bold border ${method === m.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{m.short}</button>
            ))}
          </div>
          <div><label className="label-eyebrow block mb-1">Monto recibido</label>
            <input type="number" min="0" step="1000" className="input-flat font-mono" data-testid="ortho-pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="flex justify-end">
            <button onClick={pay} disabled={loading || cashOpen === false || !(Number(amount) > 0)} className="btn-primary disabled:opacity-60" data-testid="ortho-pay-confirm">
              {loading ? "Registrando…" : `Cobrar ${formatMoney(Number(amount) || 0)}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrthoInstallments({ caseData, installments, onChange }) {
  const { user } = useAuth();
  const [planOpen, setPlanOpen] = useState(false);
  const [payInst, setPayInst] = useState(null);
  const pp = caseData.payment_plan || {};
  const [plan, setPlan] = useState({ initial_fee: pp.initial_fee || "", monthly_fee: pp.monthly_fee || "", installments_count: pp.installments_count || caseData.estimated_months || 18, first_due_date: pp.first_due_date || todayIso() });
  const [saving, setSaving] = useState(false);

  const paid = installments.filter((i) => i.status === "pagado").reduce((s, i) => s + (i.paid_amount || i.amount), 0);
  const total = installments.reduce((s, i) => s + i.amount, 0);
  const overdue = installments.filter((i) => i.status === "vencido");
  const pct = total ? Math.round((paid / total) * 100) : 0;

  const savePlan = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/ortho/${caseData.id}/plan`, { ...plan, initial_fee: Number(plan.initial_fee) || 0, monthly_fee: Number(plan.monthly_fee) || 0, installments_count: Number(plan.installments_count) || 0 });
      toast.success("Plan de pagos generado"); setPlanOpen(false); onChange && onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const unpay = async (i) => {
    if (!window.confirm(`¿Revertir el pago de ${i.label}? Se eliminará el movimiento de caja.`)) return;
    try { await api.post(`/ortho/installments/${i.id}/unpay`); toast.success("Pago revertido"); onChange && onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <section className="space-y-3" data-testid="ortho-installments">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">Financiero</div>
          <h3 className="font-display font-bold text-xl tracking-tight">Plan de pagos</h3>
        </div>
        <button onClick={() => setPlanOpen((v) => !v)} className="h-10 px-3 rounded-sm border border-border text-sm hover:bg-muted flex items-center gap-2" data-testid="ortho-plan-toggle">
          <Settings2 className="w-4 h-4" /> {installments.length ? "Ajustar plan" : "Configurar cuotas"}
        </button>
      </div>

      {planOpen && (
        <form onSubmit={savePlan} className="card-flat p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="ortho-plan-form">
          <div><label className="label-eyebrow block mb-1">Cuota inicial</label><input type="number" step="1000" min="0" className="input-flat font-mono" data-testid="plan-initial" value={plan.initial_fee} onChange={(e) => setPlan({ ...plan, initial_fee: e.target.value })} /></div>
          <div><label className="label-eyebrow block mb-1">Mensualidad</label><input type="number" step="1000" min="0" required className="input-flat font-mono" data-testid="plan-monthly" value={plan.monthly_fee} onChange={(e) => setPlan({ ...plan, monthly_fee: e.target.value })} /></div>
          <div><label className="label-eyebrow block mb-1">N° de cuotas</label><input type="number" min="0" max="60" required className="input-flat font-mono" data-testid="plan-count" value={plan.installments_count} onChange={(e) => setPlan({ ...plan, installments_count: e.target.value })} /></div>
          <div><label className="label-eyebrow block mb-1">Primer vencimiento</label><input type="date" required className="input-flat" data-testid="plan-first-due" value={plan.first_due_date} onChange={(e) => setPlan({ ...plan, first_due_date: e.target.value })} /></div>
          <button type="submit" disabled={saving} className="btn-primary justify-center" data-testid="plan-save">{saving ? "Generando…" : "Generar"}</button>
          <p className="col-span-2 md:col-span-5 text-xs text-muted-foreground">Total: <b className="font-mono">{formatMoney((Number(plan.initial_fee) || 0) + (Number(plan.monthly_fee) || 0) * (Number(plan.installments_count) || 0))}</b>. Las cuotas ya pagadas se conservan; las pendientes se regeneran.</p>
        </form>
      )}

      {installments.length > 0 && (
        <div className="card-flat p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="label-eyebrow">Pagado</div>
              <div className="font-mono text-2xl font-bold" data-testid="ortho-paid-total">{formatMoney(paid)} <span className="text-sm text-muted-foreground font-normal">/ {formatMoney(total)}</span></div>
            </div>
            {overdue.length > 0 && (
              <div className="text-xs font-bold text-destructive flex items-center gap-1" data-testid="ortho-overdue-badge"><AlertTriangle className="w-4 h-4" /> {overdue.length} cuota(s) vencida(s) · {formatMoney(overdue.reduce((s, i) => s + i.amount, 0))}</div>
            )}
          </div>
          <div className="h-2 bg-muted rounded-full mt-3 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        {installments.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">Configura la cuota inicial y las mensualidades para generar el calendario de pagos.</div>}
        {installments.map((i) => {
          const st = INST_STATUS[i.status] || INST_STATUS.pendiente;
          return (
            <div key={i.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40" data-testid={`ortho-inst-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{i.label}</div>
                <div className="font-mono text-xs text-muted-foreground">Vence {fmtDate(i.due_date)}{i.status === "pagado" && ` · Pagada ${fmtDate(i.paid_at)} · ${i.payment_method}`}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${st.cls}`}>{st.label}</span>
              <div className="font-mono text-sm w-24 text-right">{formatMoney(i.status === "pagado" ? (i.paid_amount ?? i.amount) : i.amount)}</div>
              {i.status !== "pagado" ? (
                <button onClick={() => setPayInst(i)} className="h-8 px-3 rounded-sm bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-bold" data-testid={`ortho-pay-btn-${i.id}`}>Pagar</button>
              ) : user?.role === "admin" ? (
                <button onClick={() => unpay(i)} className="h-8 w-8 rounded-sm border border-border text-muted-foreground hover:text-destructive flex items-center justify-center" title="Revertir pago" aria-label="Revertir pago"><RotateCcw className="w-3.5 h-3.5" /></button>
              ) : <div className="w-8" />}
            </div>
          );
        })}
      </div>

      <PayDialog inst={payInst} onOpenChange={(o) => { if (!o) setPayInst(null); }} onDone={onChange} />
    </section>
  );
}
